import { storage, AsyncStorage, StorageSchema } from '../../utils/storage'
import { isObject } from '../../utils'

import { TelemetrySession } from './session'

import { SDK_TELEMETRY_STORAGE_KEY } from '../../constants'
import { PlaybackTelemetryConfig, StoreRecord, TelemetryEvent } from './types'
import { compile, STORE_NAMES, TELEMETRY_INDEXES } from './utils'

import { MissingTelemetrySessionError, NotImplemented } from '../../errors'
import { EVENT_PLAYBACK_STATUS } from '../../constants/enums'

export abstract class PlaybackTelemetry<Data, Schema extends StorageSchema, Event extends TelemetryEvent<unknown>> {
  #session?: TelemetrySession<Data>
  #store: StoreRecord<Event>[]
  #config: PlaybackTelemetryConfig
  #storage!: AsyncStorage<Schema>

  get session() {
    return this.#session
  }

  get store() {
    return this.#store
  }

  get config() {
    return this.#config
  }

  get storage() {
    return this.#storage
  }

  constructor(config?: PlaybackTelemetryConfig, session?: TelemetrySession<Data>) {
    this.#config = Object.assign({ autosave: true, storage: 'auto' }, config)
    this.#store = []

    if (session) this.add(session as TelemetrySession<Data>)
  }

  public reset() {
    this.#store = []
  }

  public remove<T>(data: T, primaryKey = 'id') {
    this.#store = this.store.filter(event => Array.isArray(data) ?
      !data.map(i => isObject(i) ? i[primaryKey] : i).includes(event[primaryKey as never]) :
      (isObject(data) ? data[primaryKey as never] : data) !== event[primaryKey as never])
  }

  public async create() {
    if (!this.#storage) {
      this.#storage = await storage(SDK_TELEMETRY_STORAGE_KEY, STORE_NAMES.map(name =>
        ({ name, options: { keyPath: 'id' }, indexes: TELEMETRY_INDEXES })), 2, this.config.storage === 'ls')
    }
  }

  public add(data: TelemetrySession<Data>) {
    if (data?.constructor?.name === 'TelemetrySession' || data instanceof TelemetrySession) {
      this.#session = data as TelemetrySession<Data>
    } else throw new MissingTelemetrySessionError

    return this
  }

  public replace<T>(event: Event) {
    if (!this.session) return -1

    const index = this.store.findIndex(e => e.id === event.id)
    if (index != -1) this.#store[index] = compile<Event, Data, T | unknown>(event, this.session)

    return index
  }

  public push(event: Event) {
    if (!this.session) return [event, null]

    const payload = compile(event, this.session)

    this.store.push(payload)

    return [event as Event, payload]
  }

  protected emitInitiated() {
    throw new NotImplemented
  }
}

export { EVENT_PLAYBACK_STATUS, TelemetrySession }
